import React, { useEffect, useState } from 'react';
import {
    Grid,
    TextField,
    Button,
    Dialog,
    DialogContent,
    Typography,
} from '@mui/material';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';
import AppDialogTitle from '../../../ui/appDialogTitle';
import { isLocalDev } from '../../../../utils/environment';
import { ValidDataService } from '../../../../service/validDataService';
import SupplierListDialog from '../../ValidData/validSupplier/supplierListDialog';
import frameValidationSchema, { initialValuesFrameForm } from './frameValidationSchema';
import SectionTitle from '../../../ui/sectionTitle';
import SimpleDatePicker from '../../../ui/simpleDatePicker';
import BrandListDialog from '../../ValidData/validSupplier/brandListDialog';
import ValidFrameBasicInfo from './validFrameBasicInfoForm';
import ValidFrameCostAndPricingForm from './validFrameCostAndPricingForm';
import ValidFramePropertiesForm from './validFramePropertiesForm';
import { ACCEPTED_IMAGE_FILE_FORMAT, formatFileSize } from '../../../../utils';
import AttachmentUploader from '../../../common/attachmentUploader';
import { AttachmentService } from '../../../../service/attachmentService';
import validLookup from '../../../../utils/validLookup';

const MAX_FILE_SIZE = 10 * 1024 * 1024;
const MAX_NO_OF_FILES = 10;

export default function ValidFrameEditForm({ open, onClose, onRecordUpdated, onRecordCopied, recordToEdit, isCopy = false }) {
    const [supplierDialogOpen, setSupplierDialogOpen] = useState(false);
    const [brandDialogOpen, setBrandDialogOpen] = useState(false);
    const [attachments, setAttachments] = useState([])

    const formik = useFormik({
        initialValues: {
            ...initialValuesFrameForm,
            ...recordToEdit, 
            modelNumber: recordToEdit.modelNumber || '',
            sku: recordToEdit.sku || '',
            catalogProductId: recordToEdit.catalogProductId || null,
            supplierName: recordToEdit.supplier?.supplierName || '',
            brand: recordToEdit.brand?.name || '',
            supplierCost: recordToEdit.supplierCost ? recordToEdit.supplierCost : '',
            landingCost: recordToEdit.landingCost ? recordToEdit.landingCost : '',
            listPrice: recordToEdit.listPrice ? recordToEdit.listPrice : '',
            properties: {
                // Backend now sends normalized flat properties object
                eyeSize: recordToEdit.properties?.eyeSize || '',
                bridgeSize: recordToEdit.properties?.bridgeSize || '',
                templeLength: recordToEdit.properties?.templeLength || recordToEdit.properties?.temple || '',
                aMeasurement: recordToEdit.properties?.aMeasurement || recordToEdit.properties?.a || '',
                bMeasurement: recordToEdit.properties?.bMeasurement || recordToEdit.properties?.b || '',
                dblMeasurement: recordToEdit.properties?.dblMeasurement || recordToEdit.properties?.dbl || '',
                edMeasurement: recordToEdit.properties?.edMeasurement || recordToEdit.properties?.ed || '',
                circumference: recordToEdit.properties?.circumference || '',
                edAngle: recordToEdit.properties?.edAngle || '',
                frameColor: recordToEdit.properties?.color || '',
                frameColorCode: recordToEdit.properties?.colorCode || '',
                lensColor: '',
                lensColorCode: '',
                material: recordToEdit.properties?.material || '',
                shape: recordToEdit.properties?.shape || '',
                gender: recordToEdit.properties?.gender || '',
                frameType: recordToEdit.properties?.frameType || '',
                color: recordToEdit.properties?.color || '',
            },
            profit: recordToEdit.profit ? recordToEdit.profit : '',
            profitPercentage: recordToEdit.profitPercentage ? recordToEdit.profitPercentage : '',
            lastOrdered: recordToEdit.lastOrdered ? recordToEdit.lastOrdered  : '',
            lastReceived: recordToEdit.lastReceived ? recordToEdit.lastReceived : '',
            lastSold: recordToEdit.lastSold ? recordToEdit.lastSold  : '',
            totalSold: recordToEdit.totalSold ? recordToEdit.totalSold  : '',
            returned: recordToEdit.returned ? recordToEdit.returned : '',
            quantityOnOrder: recordToEdit.quantityOnOrder ? recordToEdit.quantityOnOrder : '',
            quantityOnHand: recordToEdit.quantityOnHand ? recordToEdit.quantityOnHand :'',            
        },
        validationSchema: frameValidationSchema,
        onSubmit: (values) => {
            onSubmit(values);
        },
    });
    const { values, setFieldValue, handleChange, handleSubmit, touched } = formik;

    useEffect(() => {
        
        (async () => {
            if (!recordToEdit || !recordToEdit.id) return;
            const attachments = await AttachmentService.getAttachment('frame', recordToEdit.id)
            const { code, data } = attachments
            if (code === 0) {
                setAttachments(data)
            }
        })()
    }, [recordToEdit])

    const onSubmit = async (values) => {
        formik.setSubmitting(true);
        try {
            const formattedValues = {
                ...values,
                catalogProductId: values.catalogProductId || null,
                locationId: values.location.id,
                supplierId: values.supplierId || null,
                brandId: values.brandId || null,
                supplierCost: values.supplierCost === '' ? null : parseFloat(values.supplierCost),
                landingCost: values.landingCost === '' ? null : parseFloat(values.landingCost),
                listPrice: values.listPrice === '' ? null : parseFloat(values.listPrice),
                photos: values.photos === '' ? null : values.photos,
                type: 'frame',
                // Only send properties if NOT from catalog (catalog properties come from catalog)
                properties: values.catalogProductId ? undefined : {
                    ...values.properties,
                    eyeSize: values.properties.eyeSize === '' ? null : parseFloat(values.properties.eyeSize),
                    bridgeSize: values.properties.bridgeSize === '' ? null : parseFloat(values.properties.bridgeSize),
                    templeLength: values.properties.templeLength === '' ? null : parseFloat(values.properties.templeLength),
                    aMeasurement: values.properties.aMeasurement === '' ? null : parseFloat(values.properties.aMeasurement),
                    bMeasurement: values.properties.bMeasurement === '' ? null : parseFloat(values.properties.bMeasurement),
                    dblMeasurement: values.properties.dblMeasurement === '' ? null : parseFloat(values.properties.dblMeasurement),
                    edMeasurement: values.properties.edMeasurement === '' ? null : parseFloat(values.properties.edMeasurement),
                    circumference: values.properties.circumference === '' ? null : parseFloat(values.properties.circumference),
                    edAngle: values.properties.edAngle === '' ? null : parseFloat(values.properties.edAngle),
                },
                profit: values.profit === '' ? null : parseFloat(values.profit),
                profitPercentage: values.profitPercentage === '' ? null : parseFloat(values.profitPercentage),
                lastOrdered: values.lastOrdered === '' ? null : values.lastOrdered,
                lastReceived: values.lastReceived === '' ? null : values.lastReceived,
                lastSold: values.lastSold === '' ? null : values.lastSold,
                totalSold: values.totalSold === '' ? null : parseInt(values.totalSold, 10),
                returned: values.returned === '' ? null : parseInt(values.returned, 10),
                quantityOnOrder: values.quantityOnOrder === '' ? null : parseInt(values.quantityOnOrder, 10),
                quantityOnHand: values.quantityOnHand === '' ? null : parseInt(values.quantityOnHand, 10),
            };

            let response
            if (isCopy) {
                formattedValues.id = null; // Ensure id is null for copy
                formattedValues.properties.id = null; // Ensure properties id is null for copy
                response = await ValidDataService.addInventory(formattedValues);
            } else {
                formattedValues.id = recordToEdit.id; // Use the existing id for update
                response = await ValidDataService.updateInventory(formattedValues);
            }
            
            const { code, data, message } = response;

            formik.setSubmitting(false);

            if (code === 0) {
                toast.success(message);
                onClose()
                if (isCopy) {
                    onRecordCopied(data);
                } else 
                    onRecordUpdated(data);
            } else {
                toast.error(message);
            }
        } catch (error) {
            if (isLocalDev) {
                console.error(error);
            }
            formik.setSubmitting(false);
            toast.error('Failed to update record');
        }
    };

    const handleSupplierSelect = (supplier) => {
        setFieldValue('supplierId', supplier.id);
        setFieldValue('supplierName', supplier.supplierName);
        // reset the brand
        setFieldValue('brand', '');
    };

    const handleBrandSelect = (brand) => {
        setFieldValue('brand', brand.name);
        setFieldValue('brandId', brand.id);
    };

    return (
        <Dialog open={open} maxWidth={'lg'} fullWidth>
            <AppDialogTitle title={`${isCopy ? 'Copy' : 'Edit'} Frame`} onClose={onClose} />

            <DialogContent>
                <form onSubmit={handleSubmit}>
                    <Grid container spacing={2}>
                        {/* Section 1: Basic Information */}
                        <ValidFrameBasicInfo 
                            formik={formik} 
                            setBrandDialogOpen={setBrandDialogOpen} 
                            setSupplierDialogOpen={setSupplierDialogOpen} 
                            labels={{modelNumber: null}}
                            disableModelNumber={!isCopy}
                        />

                        {/* <Grid container spacing={2} mt={2}> */}
                        <Grid item xs={12} sm={4}>
                            <ValidFrameCostAndPricingForm formik={formik} />
                        </Grid>

                        <Grid item xs={12} sm={8}>
                            <ValidFramePropertiesForm formik={formik} />
                        </Grid>

                        {/* </Grid> */}


                        {/* Section 5: Inventory and Status */}
                        <Grid item xs={12}>
                            <SectionTitle title="Inventory and Status" />
                        </Grid>
                        <Grid item xs={12} sm={3} mt={1}>
                            <SimpleDatePicker
                                label={"Last Ordered"}
                                onChange={(value) => setFieldValue('lastOrdered', value)}
                                value={values.lastOrdered}
                            />
                        </Grid>
                        <Grid item xs={12} sm={3} mt={1}>
                            <SimpleDatePicker
                                label={"Last Received"}
                                onChange={(value) => setFieldValue('lastReceived', value)}
                                value={values.lastReceived}
                            />
                        </Grid>
                        <Grid item xs={12} sm={3} mt={1}>
                            <SimpleDatePicker
                                label={"Last Sold"}
                                onChange={(value) => setFieldValue('lastSold', value)}
                                value={values.lastSold}
                            />
                        </Grid>
                        <Grid item xs={12} sm={2} mt={1}>
                            <TextField
                                value={values.totalSold}
                                onChange={handleChange}
                                name="totalSold"
                                label="Total Sold"
                                type="number"
                                size='small'
                                fullWidth
                                disabled
                            />
                        </Grid>
                        <Grid item xs={12} sm={2} mt={1}>
                            <TextField
                                value={values.returned}
                                onChange={handleChange}
                                name="returned"
                                label="Returned"
                                type="number"
                                size='small'
                                fullWidth
                                disabled
                            />
                        </Grid>
                        <Grid item xs={12} sm={2} mt={1}>
                            <TextField
                                value={values.quantityOnOrder}
                                onChange={handleChange}
                                name="quantityOnOrder"
                                label="Quantity On Order"
                                type="number"
                                size='small'
                                fullWidth
                                error={touched.quantityOnOrder && Boolean(formik.errors.quantityOnOrder)}
                                helperText={touched.quantityOnOrder && formik.errors.quantityOnOrder}
                            />
                        </Grid>
                        <Grid item xs={12} sm={2} mt={1}>
                            <TextField
                                value={values.quantityOnHand}
                                onChange={handleChange}
                                name="quantityOnHand"
                                label="Quantity On Hand"
                                type="number"
                                size='small'
                                fullWidth
                                error={touched.quantityOnHand && Boolean(formik.errors.quantityOnHand)}
                                helperText={touched.quantityOnHand && formik.errors.quantityOnHand}
                            />
                        </Grid>

                        <Grid item xs={12} mt={1}>
                            <TextField
                                value={values.notes}
                                onChange={handleChange}
                                name="notes"
                                label="Notes"
                                multiline
                                rows={4}
                                fullWidth
                            />
                        </Grid>
                        {/* <Grid item xs={12}>
                            <Typography variant='caption'>Logo (JPEG/PNG files under {formatFileSize(MAX_FILE_SIZE)}). Max files allowed {MAX_NO_OF_FILES}</Typography>
                            <AttachmentUploader 
                                values={formik.values.photos}
                                onUpload={(files) => {
                                    formik.setFieldValue('photos', files);
                                    formik.setFieldTouched('photos', true);
                                }}
                                accept={
                                    ACCEPTED_IMAGE_FILE_FORMAT.reduce((acc, mimeType) => {
                                        acc[mimeType] = [];
                                        return acc;
                                    }, {})
                                }
                                maxFiles={MAX_NO_OF_FILES}
                                maxSize={MAX_FILE_SIZE}    
                                attachments={attachments}                            
                            />
                        </Grid> */}
                        <Grid item xs={12}>
                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                disabled={formik.isSubmitting}
                            >
                                { isCopy ? 'Create' : 'Update' }
                            </Button>
                        </Grid>
                    </Grid>
                </form>

                <SupplierListDialog
                    open={supplierDialogOpen}
                    onClose={() => setSupplierDialogOpen(false)}
                    onSelect={handleSupplierSelect} // Pass setFieldValue
                    supplierType={validLookup.supplierType.frames}
                />
                <BrandListDialog
                    open={brandDialogOpen}
                    onClose={() => setBrandDialogOpen(false)}
                    onSelect={handleBrandSelect}
                    supplierId={values.supplierId}
                    type='frames'
                    initialFilter={values.brand}
                />
            </DialogContent>
        </Dialog>
    );
};